import moment from 'moment'
import type { IntlShape } from 'react-intl'
import type { CharterBundleNights } from 'constants/charterFlight'
import { CHARTER_NIGHTS_TO_DURATION_DAYS, MAX_ROOMS_LEFT_TO_SHOW_INVENTORY, MAX_SEATS_LEFT_TO_SHOW_INVENTORY } from 'constants/charterFlight'
import { fareClassToApiCabinMap, FlightsClassTypes } from 'constants/flight'
import { getSumExtraGuestsSurcharge, allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import { isBundlePackageWithPrice } from 'lib/offer/offerTypes'
import type { DateRange } from './types'

/**
 * Extract the minimum seatsLeft from all flights in a journey
 * The inventory is stored per flight, so we need to find the minimum across all flights
 *
 * Data structure: result.onewayFares[i].fares[j].flightGroups[k].flights[m].seatsLeft
 */
export function getSeatsLeftFromJourney(journey?: App.JourneyV2): number | undefined {
  if (!journey?.flightGroup?.flights) {
    return undefined
  }

  const seatsLeftValues = journey.flightGroup.flights
    .map(flight => flight.seatsLeft)
    .filter((seats): seats is number => typeof seats === 'number' && seats >= 0)

  if (seatsLeftValues.length === 0) {
    return undefined
  }

  return Math.min(...seatsLeftValues)
}

export function getJourneyTotalFareForFareClass({
  journey,
  fareClass,
}: {
  journey?: App.JourneyV2
  fareClass: FlightsClassTypes
}): number | undefined {
  if (!journey) {
    return undefined
  }
  if (fareClass === FlightsClassTypes.ECONOMY) {
    return journey.price.all.totalFare
  }
  const cabin = fareClassToApiCabinMap[fareClass]
  const upsellFare = journey.upsellFares?.find(upsell => upsell.cabin === cabin)
  const upsellTotal = upsellFare?.cost?.all
  if (typeof upsellTotal === 'number' && upsellTotal > 0) {
    return upsellTotal
  }
  return journey.price.all.totalFare
}

/**
 * Find matching returning flight based on returnDate
 *
 * This function tries to match a returning flight by comparing various date fields
 * (departingDate, departureDateTime, arrivalDate, arrivalDateTime) with the returnDate.
 * The returnDate could be either the departure date or arrival date from MLE.
 *
 * @param returningFlights - Array of returning flight journeys
 * @param returnDate - The expected return date (ISO format string)
 * @returns The matched returning flight, or the first flight as fallback if no match found
 */
export function findMatchingReturningFlight(
  returningFlights: Array<App.JourneyV2>,
  returnDate: string | undefined,
): App.JourneyV2 | undefined {
  if (returningFlights.length === 0) {
    return undefined
  }

  let returningFlight = returningFlights[0] // Fallback to first if no match found

  if (returnDate && returningFlights.length > 0) {
    const returnDateMoment = moment(returnDate).startOf('day')

    // Try to find exact match by comparing departingDate or arrivalDate
    // returnDate could be either the departure date or arrival date from MLE
    const matchedFlight = returningFlights.find(fare => {
      const firstFlight = fare?.flightGroup?.flights?.[0]
      if (!firstFlight) return false

      // Try matching by departingDate (departure from MLE)
      const flightDepartingDate = firstFlight.departingDate
      if (flightDepartingDate) {
        const flightDateMoment = moment(flightDepartingDate).startOf('day')
        if (flightDateMoment.isSame(returnDateMoment)) {
          return true
        }
      }

      // Try matching by departureDateTime if departingDate is not available
      const departureDateTime = firstFlight.departureDateTime
      if (departureDateTime) {
        const flightDateMoment = moment(departureDateTime).startOf('day')
        if (flightDateMoment.isSame(returnDateMoment)) {
          return true
        }
      }

      // Try matching by arrivalDate (arrival at MEL)
      const flightArrivalDate = firstFlight.arrivalDate
      if (flightArrivalDate) {
        const flightDateMoment = moment(flightArrivalDate).startOf('day')
        if (flightDateMoment.isSame(returnDateMoment)) {
          return true
        }
      }

      // Try matching by arrivalDateTime if arrivalDate is not available
      const arrivalDateTime = firstFlight.arrivalDateTime
      if (arrivalDateTime) {
        const flightDateMoment = moment(arrivalDateTime).startOf('day')
        if (flightDateMoment.isSame(returnDateMoment)) {
          return true
        }
      }

      return false
    })

    if (matchedFlight) {
      returningFlight = matchedFlight
    }
  }

  return returningFlight
}

/**
 * Extract prices, fees, and roomsLeft from hotel offers
 *
 * This function processes hotel offers to extract:
 * - Lowest base price (regular and member prices) - matching useSearchPrices charterBundle logic
 * - All fees (propertyFees, surcharge, taxesAndFees, extraGuestSurcharges)
 * - Minimum roomsLeft across all offers
 *
 * Price calculation matches useSearchPrices charterBundle logic:
 * - basePrice = (package.price ?? 0) * numberOfRooms (for charterBundle, only base price is used)
 * - Fees are extracted separately and match useSearchPrices fees structure
 *
 * @param offers - Array of hotel offers with packages
 * @param nights - Number of nights to match package duration
 * @param numberOfRooms - Number of rooms to multiply price by (package.price is per room)
 * @param rooms - Array of occupancies for calculating extraGuestSurcharges
 * @param offerMetaData - Metadata containing availableRooms information
 * @returns Object containing lowestPrice, lowestMemberPrice, fees, and roomsLeft
 */
export function extractPricesAndRoomsLeftFromOffers(
  offers: Array<App.HotelOffer>,
  nights: number,
  numberOfRooms: number,
  rooms: Array<App.Occupants>,
  offerMetaData: Record<string, App.OfferListMetaData | undefined> | undefined,
): {
  lowestPrice: number | undefined
  lowestMemberPrice: number | undefined
  lowestPropertyFees: number | undefined
  lowestSurcharge: number | undefined
  lowestTaxesAndFees: number | undefined
  lowestExtraGuestSurcharges: number | undefined
  roomsLeft: number | undefined
} {
  const prices: Array<number> = []
  const memberPrices: Array<number> = []
  const propertyFeesValues: Array<number> = []
  const surchargeValues: Array<number> = []
  const taxesAndFeesValues: Array<number> = []
  const extraGuestSurchargesValues: Array<number> = []
  const roomsLeftValues: Array<number> = []

  offers.forEach(offer => {
    const matchingPackage = offer.packages.find(pkg => pkg.duration === nights)

    if (!matchingPackage) return

    // Get availableRooms from metaData (not from package)
    const metaData = offerMetaData?.[offer.id]

    // Check if offer is available in metadata
    // If metadata indicates unavailable, skip this offer
    if (metaData?.available === false) {
      return
    }

    // Check if package capacity fits the current room occupancy
    // This ensures we only use prices from packages that can accommodate the selected rooms
    // For example, if selecting 2 adults + 1 infant, we should only consider packages that support this occupancy
    const capacityValid = rooms.every(room =>
      allRoomsOccupantsFitRoomCapacity(
        room,
        matchingPackage.roomRate?.capacities ?? [],
        offer,
      ),
    )

    // Skip this package if capacity doesn't fit the current occupancy
    if (!capacityValid) {
      return
    }

    // Check if there are available rooms
    // Skip if availableRooms is 0 or undefined (when we have metadata)
    if (metaData?.availableRooms !== undefined && metaData.availableRooms === 0) {
      return
    }

    // Calculate extraGuestSurcharges (same logic as useSearchPrices)
    // For bundle packages, extraGuestSurcharges should be 0
    const extraGuestSurcharges = isBundlePackageWithPrice(matchingPackage)
      ? 0
      : getSumExtraGuestsSurcharge(rooms, offer, matchingPackage)

    // Calculate price multiplier (same as useSearchPrices: rate ? 1 : numberOfRooms)
    // Since we don't have rate in calendar item context, always use numberOfRooms
    const priceMultiplier = numberOfRooms

    // Extract base price only (matching useSearchPrices charterBundle logic)
    // For charterBundle, useSearchPrices uses: (actualPackage.price ?? 0) * charterBundlePriceMultiplier
    if (matchingPackage.price > 0) {
      const basePrice = matchingPackage.price * priceMultiplier
      prices.push(basePrice)

      // Extract all fees (matching useSearchPrices fees structure)
      const propertyFees = (matchingPackage.propertyFees ?? 0) * priceMultiplier
      const surcharge = (matchingPackage.surcharge ?? 0) * priceMultiplier
      const taxesAndFees = (matchingPackage.taxesAndFees ?? 0) * priceMultiplier

      propertyFeesValues.push(propertyFees)
      surchargeValues.push(surcharge)
      taxesAndFeesValues.push(taxesAndFees)
      extraGuestSurchargesValues.push(extraGuestSurcharges)

      // Get roomsLeft from metaData (OfferListMetaData.availableRooms)
      if (metaData?.availableRooms !== undefined && metaData.availableRooms > 0) {
        roomsLeftValues.push(metaData.availableRooms)
      }
    }

    // Extract member base price only
    if (matchingPackage.memberPrice && matchingPackage.memberPrice > 0) {
      const baseMemberPrice = matchingPackage.memberPrice * priceMultiplier
      memberPrices.push(baseMemberPrice)
    }
  })

  const lowestPrice = prices.length > 0 ? Math.min(...prices) : undefined
  const lowestMemberPrice = memberPrices.length > 0 ? Math.min(...memberPrices) : undefined
  // For fees, we'll use the minimum values (they should be similar across offers)
  // In practice, we might want to match fees with the offer that has the lowest price
  const lowestPropertyFees = propertyFeesValues.length > 0 ? Math.min(...propertyFeesValues) : undefined
  const lowestSurcharge = surchargeValues.length > 0 ? Math.min(...surchargeValues) : undefined
  const lowestTaxesAndFees = taxesAndFeesValues.length > 0 ? Math.min(...taxesAndFeesValues) : undefined
  const lowestExtraGuestSurcharges = extraGuestSurchargesValues.length > 0 ? Math.min(...extraGuestSurchargesValues) : undefined
  // Get minimum roomsLeft from all offers (similar to seatsLeft logic)
  const roomsLeft = roomsLeftValues.length > 0 ? Math.min(...roomsLeftValues) : undefined

  return {
    lowestPrice,
    lowestMemberPrice,
    lowestPropertyFees,
    lowestSurcharge,
    lowestTaxesAndFees,
    lowestExtraGuestSurcharges,
    roomsLeft,
  }
}

/**
 * Calculate bundle prices from hotel and flight data
 *
 * This function combines hotel prices with flight prices to calculate:
 * - Total bundle price (hotel + flight)
 * - Price per person
 * - Member prices (if available)
 *
 * @param params - Parameters containing hotel/flight data and passenger count
 * @returns CalendarItemBundleData with calculated bundle prices
 */
export function calculateBundlePrices(params: {
  hotelFetching: boolean
  flightFetching: boolean
  lowestPrice: number | undefined
  lowestMemberPrice: number | undefined
  totalFlightFare: number
  totalPassengers: number
  seatEligiblePassengers: number
  seatsLeft: number | undefined
  roomsLeft: number | undefined
  offerCount: number
}): {
  fetching: boolean
  available: boolean
  totalPrice: number
  pricePerPerson: number
  memberTotalPrice: number | undefined
  memberPricePerPerson: number | undefined
  hotelPrice: number | undefined
  hotelMemberPrice: number | undefined
  flightPrice: number
  offerCount: number
  seatsLeft: number | undefined
  roomsLeft: number | undefined
} {
  const {
    hotelFetching,
    flightFetching,
    lowestPrice,
    lowestMemberPrice,
    totalFlightFare,
    totalPassengers,
    seatEligiblePassengers,
    seatsLeft,
    roomsLeft,
    offerCount,
  } = params

  // Calculate bundle prices
  const bothDataReady = !hotelFetching && !flightFetching && lowestPrice && totalFlightFare > 0

  let bundleTotalPrice: number | undefined
  let bundleMemberTotalPrice: number | undefined
  let bundlePricePerPerson: number | undefined
  let bundleMemberPricePerPerson: number | undefined

  if (bothDataReady) {
    bundleTotalPrice = lowestPrice + totalFlightFare
    bundlePricePerPerson = totalPassengers > 0 ? bundleTotalPrice / totalPassengers : undefined

    if (lowestMemberPrice) {
      bundleMemberTotalPrice = lowestMemberPrice + totalFlightFare
      bundleMemberPricePerPerson = totalPassengers > 0 ? bundleMemberTotalPrice / totalPassengers : undefined
    }
  }

  // Seats must be sufficient for the selected number of seat-eligible passengers (exclude infants) when we have seat data
  const seatsSufficient = hasSufficientSeats(seatsLeft, seatEligiblePassengers)

  return {
    fetching: hotelFetching || flightFetching,
    available: !!bothDataReady && seatsSufficient,
    totalPrice: bundleTotalPrice ?? 0,
    pricePerPerson: bundlePricePerPerson ?? 0,
    memberTotalPrice: bundleMemberTotalPrice,
    memberPricePerPerson: bundleMemberPricePerPerson,
    hotelPrice: lowestPrice,
    hotelMemberPrice: lowestMemberPrice,
    flightPrice: totalFlightFare,
    offerCount,
    seatsLeft,
    roomsLeft,
  }
}

/**
 * Returns true when seat inventory is sufficient for the given number of seat-eligible passengers.
 * - If seatsLeft is undefined, we optimistically assume sufficient (loading / not provided).
 */
export function hasSufficientSeats(seatsLeft: number | undefined, seatEligiblePassengers: number): boolean {
  return typeof seatsLeft === 'number' ? seatsLeft >= seatEligiblePassengers : true
}

/**
 * Calculates the inventory message based on seats and rooms availability.
 *
 * Logic:
 * 1. If both seats <= 10 AND rooms <= 5: prioritize showing seats
 * 2. If seats > 10 but rooms <= 5: show rooms
 * 3. If only seats are low (<= 10): show seats
 * 4. If only rooms are low (<= 5): show rooms
 *
 * @param seatsLeft - Number of seats left (undefined if not available)
 * @param roomsLeft - Number of rooms left (undefined if not available)
 * @returns Inventory message string, or empty string if no message should be shown
 */
export function getInventoryMessage(intl: IntlShape, seatsLeft: number | undefined, roomsLeft: number | undefined): string {
  const hasSeatsData = typeof seatsLeft === 'number'
  const hasRoomsData = typeof roomsLeft === 'number'

  // If either is 0, show unavailable
  if ((hasSeatsData && seatsLeft === 0)) {
    return intl.formatMessage({
      id: 'UbNyPnEgfd',
      defaultMessage: 'Bundle sold out',
      description: 'Inventory message when all charter bundle seats are sold out',
    })
  }
  if ((hasRoomsData && roomsLeft === 0)) {
    return intl.formatMessage({
      id: 'iYsZT1Xool',
      defaultMessage: 'Hotel sold out',
      description: 'Inventory message when all hotel rooms are sold out',
    })
  }

  const seatsLow =
    hasSeatsData &&
    seatsLeft > 0 &&
    seatsLeft <= MAX_SEATS_LEFT_TO_SHOW_INVENTORY

  const roomsLow =
    hasRoomsData &&
    roomsLeft > 0 &&
    roomsLeft <= MAX_ROOMS_LEFT_TO_SHOW_INVENTORY

  const seatsMessage = intl.formatMessage(
    {
      id: '23YWa0/2Zj',
      defaultMessage: '{count, plural, one {# seat left at this price} other {# seats left at this price}}',
      description: 'Urgency message showing limited seats remaining for charter flight',
    },
    { count: seatsLeft },
  )

  const roomsMessage = intl.formatMessage(
    {
      id: 'UAOC3Gw+me',
      defaultMessage: '{count, plural, one {# room left at this price} other {# rooms left at this price}}',
      description: 'Urgency message showing limited hotel rooms remaining',
    },
    { count: roomsLeft },
  )

  if (hasSeatsData && hasRoomsData) {
    if (seatsLow && roomsLow) {
      return seatsMessage
    }
    if (!seatsLow && roomsLow) {
      return roomsMessage
    }
    if (seatsLow) {
      return seatsMessage
    }
  } else if (seatsLow) {
    return seatsMessage
  } else if (roomsLow) {
    return roomsMessage
  }

  return ''
}

export type CTAMessageType = 'hotel-sold-out' | 'bundle-sold-out' | 'none'

/**
 * Determines the CTA (Call to Action) message type based on inventory availability.
 *
 * @param seatsLeft - Number of seats left (undefined if not available)
 * @param roomsLeft - Number of rooms left (undefined if not available)
 * @returns CTA message type: 'hotel-sold-out', 'bundle-sold-out', or 'none'
 */
export function getCTAMessage(seatsLeft: number | undefined, roomsLeft: number | undefined): CTAMessageType {
  const hasSeatsData = typeof seatsLeft === 'number'
  const hasRoomsData = typeof roomsLeft === 'number'

  // Check if flights are sold out (seats = 0 or undefined)
  if ((hasSeatsData && seatsLeft === 0)) {
    return 'bundle-sold-out'
  }

  // Check if hotel is sold out (rooms = 0 or undefined)
  if ((hasRoomsData && roomsLeft === 0)) {
    return 'hotel-sold-out'
  }

  return 'none'
}

/**
 * Calculate the lowest available hotel price from a list of offer IDs
 *
 * This function filters offers based on availability and capacity constraints:
 * - Checks if offer is available in metadata
 * - Verifies package capacity fits the current occupancy
 * - Ensures there are available rooms
 * - Returns the lowest price (member price if luxPlusEnabled, otherwise regular price)
 *
 * @param params - Parameters for price calculation
 * @param params.offerIds - Array of offer IDs to process
 * @param params.allOffers - Map of all offers by offer ID
 * @param params.offerMetaData - Map of offer metadata by offer ID
 * @param params.rooms - Array of occupancies to check capacity against
 * @param params.nights - Number of nights to match package duration
 * @param params.luxPlusEnabled - Whether to prefer member prices
 * @returns Lowest available hotel price, or 0 if no valid price found
 */
export function getLowestAvailableHotelPrice(params: {
  offerIds: Array<string>
  allOffers: Record<string, App.Offer>
  offerMetaData: Record<string, App.OfferListMetaData | undefined> | undefined
  rooms: Array<App.Occupants>
  nights: number
  luxPlusEnabled: boolean
}): number {
  const { offerIds, allOffers, offerMetaData, rooms, nights, luxPlusEnabled } = params

  const hotelPrices = offerIds
    .map(offerId => {
      const offer = allOffers[offerId] as App.HotelOffer | undefined
      if (!offer) return null

      // Check if offer is available in metadata
      const metaData = offerMetaData?.[offerId]
      if (metaData?.available === false) {
        return null
      }

      const pkg = offer.packages.find(p => p.duration === nights)
      if (!pkg) return null

      // Check if package capacity fits the current occupancy
      // Skip if any room doesn't fit the package capacity
      const capacityValid = rooms.every(room =>
        allRoomsOccupantsFitRoomCapacity(
          room,
          pkg.roomRate?.capacities ?? [],
          offer,
        ),
      )
      if (!capacityValid) {
        return null
      }

      // Check if there are available rooms
      if (metaData?.availableRooms !== undefined && metaData.availableRooms === 0) {
        return null
      }

      // Only include price if package has valid price
      if (pkg.price > 0) {
        return luxPlusEnabled ? (pkg.memberPrice || pkg.price) : pkg.price
      }

      return null
    })
    .filter((p): p is number => typeof p === 'number' && p > 0)

  return hotelPrices.length > 0 ? Math.min(...hotelPrices) : 0
}

/**
 * Filter dateRangesByMonth by selected nights/duration
 *
 * Note: We filter by durationDays instead of nights because durationDays represents the total trip duration
 * which matches the user's selection (5 nights = ~7 days, 12 nights = ~14 days)
 *
 * @param allDateRangesByMonth - All date ranges grouped by month from charter flight journeys API
 * @param nights - Selected bundle nights (5 or 12)
 * @returns Filtered dateRangesByMonth containing only ranges matching the expected duration
 */
export function filterDateRangesByNights(
  allDateRangesByMonth: Record<string, Array<DateRange>>,
  nights: number,
): Record<string, Array<DateRange>> {
  const expectedDurationDays = CHARTER_NIGHTS_TO_DURATION_DAYS[nights as CharterBundleNights] ?? nights

  const filtered: Record<string, Array<DateRange>> = {}

  Object.entries(allDateRangesByMonth).forEach(([month, ranges]) => {
    // Filter by durationDays which represents the total trip duration
    const filteredRanges = ranges.filter(range => range.durationDays === expectedDurationDays)
    if (filteredRanges.length > 0) {
      filtered[month] = filteredRanges
    }
  })

  return filtered
}
